"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    headline: string;
    caption: string;
    money: (value: number) => string;
    nav: string;
    navStart: string;
    atStart: string;
    navEnd: string;
    distributions: string;
    distributionTotal: string;
    afterTenYears: string;
    combined: string;
    formula: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    headline: "基準価額が下がっても、分配金を合わせるとプラスになることがある",
    caption: "毎年4%稼ぎ、毎年5%分配したファンドの10年後（概念図）",
    money: (v) => `${v.toLocaleString("ja-JP")}円`,
    nav: "基準価額",
    navStart: "10,000円",
    atStart: "スタート時",
    navEnd: "9,000円",
    distributions: "分配金",
    distributionTotal: "+4,800円",
    afterTenYears: "10年後",
    combined: "合計 13,800円",
    formula: "トータルリターン = いまの基準価額 ＋ 受け取った分配金",
    title: "10年後のトータルリターン",
    description: "基準価額は下がっても、受け取った分配金を合わせれば、全体ではプラスになることがあります。",
  },
  en: {
    headline: "Even when the NAV falls, adding the distributions can leave you ahead",
    caption: "A fund earning 4% a year and distributing 5% a year, after ten years (conceptual)",
    money: (v) => `¥${v.toLocaleString("en-US")}`,
    nav: "NAV",
    navStart: "¥10,000",
    atStart: "At the start",
    navEnd: "¥9,000",
    distributions: "Distributions",
    distributionTotal: "+¥4,800",
    afterTenYears: "After 10 years",
    combined: "Total ¥13,800",
    formula: "Total return = current NAV + distributions received",
    title: "Total return after ten years",
    description:
      "Even if the NAV falls, adding the distributions you received can leave the overall result positive.",
  },
};

const C = {
  nav: COLOR.sky,
  income: COLOR.amber,
  total: COLOR.emerald,
};

const START = 10000;
const END_NAV = 9000;
const END_INCOME = 4800;
const END_TOTAL = END_NAV + END_INCOME;

function TotalReturnIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 240;

  const chartT = 62;
  const chartB = 198;
  const chartH = chartB - chartT;
  const yMax = 15000;
  const toY = (v: number) => chartB - (v / yMax) * chartH;

  const barW = 88;
  const leftX = 120;
  const rightX = 262;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      <text x={VW / 2} y={20} textAnchor="middle" fontSize="11.5" fill="currentColor" fontWeight="700">
        {t.headline}
      </text>
      <text x={VW / 2} y={34} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.caption}
      </text>

      {/* Y axis grid */}
      {[0, 5000, 10000, 15000].map((v) => (
        <g key={v}>
          <line
            x1={60}
            y1={toY(v)}
            x2={VW - 20}
            y2={toY(v)}
            stroke="currentColor"
            strokeOpacity={0.08}
            strokeDasharray="3 6"
          />
          <text x={55} y={toY(v) + 3} textAnchor="end" fontSize="7.5" fill="currentColor" fillOpacity={0.45}>
            {t.money(v)}
          </text>
        </g>
      ))}

      {/* Reference dashed line from start value across */}
      <line
        x1={leftX + barW}
        y1={toY(START)}
        x2={rightX}
        y2={toY(START)}
        stroke={C.nav}
        strokeWidth="0.8"
        strokeDasharray="3 3"
        strokeOpacity={0.45}
      />

      {/* Left bar: start */}
      <rect
        x={leftX}
        y={toY(START)}
        width={barW}
        height={chartB - toY(START)}
        rx={4}
        fill={C.nav}
        fillOpacity={0.22}
        stroke={C.nav}
        strokeWidth="1"
        strokeOpacity={0.5}
      />
      <text
        x={leftX + barW / 2}
        y={(toY(START) + chartB) / 2 - 2}
        textAnchor="middle"
        fontSize="9"
        fill={C.nav}
        fontWeight="700"
      >
        {t.nav}
      </text>
      <text
        x={leftX + barW / 2}
        y={(toY(START) + chartB) / 2 + 12}
        textAnchor="middle"
        fontSize="10"
        fill={C.nav}
        fontWeight="700"
      >
        {t.navStart}
      </text>
      <text x={leftX + barW / 2} y={chartB + 15} textAnchor="middle" fontSize="9" fill="currentColor" fontWeight="700">
        {t.atStart}
      </text>

      {/* Right bar: 10 years later, stacked */}
      {/* - NAV portion */}
      <rect
        x={rightX}
        y={toY(END_NAV)}
        width={barW}
        height={chartB - toY(END_NAV)}
        rx={4}
        fill={C.nav}
        fillOpacity={0.22}
        stroke={C.nav}
        strokeWidth="1"
        strokeOpacity={0.5}
      />
      <text
        x={rightX + barW / 2}
        y={(toY(END_NAV) + chartB) / 2 - 2}
        textAnchor="middle"
        fontSize="9"
        fill={C.nav}
        fontWeight="700"
      >
        {t.nav}
      </text>
      <text
        x={rightX + barW / 2}
        y={(toY(END_NAV) + chartB) / 2 + 12}
        textAnchor="middle"
        fontSize="10"
        fill={C.nav}
        fontWeight="700"
      >
        {t.navEnd}
      </text>

      {/* - Income portion (stacked on top) */}
      <rect
        x={rightX}
        y={toY(END_TOTAL)}
        width={barW}
        height={toY(END_NAV) - toY(END_TOTAL)}
        rx={4}
        fill={C.income}
        fillOpacity={0.3}
        stroke={C.income}
        strokeWidth="1"
        strokeOpacity={0.55}
      />
      <text
        x={rightX + barW / 2}
        y={(toY(END_TOTAL) + toY(END_NAV)) / 2 - 1}
        textAnchor="middle"
        fontSize="9"
        fill={C.income}
        fontWeight="700"
      >
        {t.distributions}
      </text>
      <text
        x={rightX + barW / 2}
        y={(toY(END_TOTAL) + toY(END_NAV)) / 2 + 12}
        textAnchor="middle"
        fontSize="10"
        fill={C.income}
        fontWeight="700"
      >
        {t.distributionTotal}
      </text>

      <text x={rightX + barW / 2} y={chartB + 15} textAnchor="middle" fontSize="9" fill="currentColor" fontWeight="700">
        {t.afterTenYears}
      </text>

      {/* Total callout above right bar */}
      <text
        x={rightX + barW / 2}
        y={toY(END_TOTAL) - 8}
        textAnchor="middle"
        fontSize="11"
        fill={C.total}
        fontWeight="800"
      >
        {t.combined}
      </text>
      <text
        x={rightX + barW / 2}
        y={toY(END_TOTAL) - 22}
        textAnchor="middle"
        fontSize="8.5"
        fill={C.total}
        fontWeight="700"
      >
        （+38%）
      </text>

      {/* Bottom equation */}
      <text
        x={VW / 2}
        y={VH - 8}
        textAnchor="middle"
        fontSize="9"
        fill="currentColor"
        fillOpacity={0.6}
        fontWeight="600"
      >
        {t.formula}
      </text>
    </svg>
  );
}

export function DocFundTotalReturnDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <TotalReturnIllustration />
    </IllustrationCard>
  );
}
